import { Eraser } from "lucide-react";
import type { RaspaditaMecanica } from "@juegossc/shared";
import {
  RASPADITA_PANEL_W,
  RASPADITA_PANEL_H,
} from "../../raspadita/raspaditaSizes";

// Mock visual de la raspadita para el preview del admin. NO es interactivo:
// alcanza con dar la idea de la forma según la mecánica (raspar un panel /
// encontrar 3 iguales / elegir una de 3). La textura es una aproximación gris
// del área raspable; lo importante para el admin es el TAMAÑO y la POSICIÓN del
// panel, que ahora usan las MISMAS medidas del lienzo (raspaditaSizes.ts) que
// la pantalla real -> coinciden. Vive dentro del wrapper centrado de
// JuegoPreview (que aplica el deslizador de posición).

// Coinciden con el ScratchGrid real (gap-2 + p-3) para que el preview muestre
// el MISMO tamaño/forma que la pantalla.
const GRID_GAP = 8;
const GRID_PADDING = 12;

// Casilla "raspable" genérica — superficie gris plateada con textura de rayado
// que imita la pintura de una raspadita sin raspar.
function ScratchSurface({
  children,
  className,
  style,
}: {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`flex items-center justify-center rounded-md text-center font-semibold text-white/70 ${className ?? ""}`}
      style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 6px, rgba(255,255,255,0.04) 6px 12px)",
        backgroundColor: "rgba(148,163,184,0.35)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function RaspaditaPreview({
  mecanica,
  iconUrl,
  panelW = RASPADITA_PANEL_W,
  panelH = RASPADITA_PANEL_H,
}: {
  mecanica: RaspaditaMecanica;
  iconUrl?: string | null;
  /** Migration 039: tamaño del panel (configurable). Default = constantes. */
  panelW?: number;
  panelH?: number;
}) {
  // Celdas RECTANGULARES (W×H), mismo cálculo que el ScratchGrid real -> el
  // preview responde a Ancho y Alto por separado, como la pantalla.
  const cellW = Math.floor((panelW - 2 * GRID_PADDING - 2 * GRID_GAP) / 3);
  const cellH = Math.floor((panelH - 2 * GRID_PADDING - 2 * GRID_GAP) / 3);
  const containerW = cellW * 3 + 2 * GRID_GAP + 2 * GRID_PADDING;
  const containerH = cellH * 3 + 2 * GRID_GAP + 2 * GRID_PADDING;
  const icon = iconUrl ? (
    <img src={iconUrl} alt="" className="h-10 w-10 object-contain" />
  ) : (
    <Eraser className="h-10 w-10 text-white/60" />
  );

  return (
    <div className="flex items-center justify-center">
      {mecanica === "panel" && (
        <ScratchSurface
          className="text-xl"
          style={{ width: panelW, height: panelH }}
        >
          Raspá acá
        </ScratchSurface>
      )}

      {mecanica === "grid" && (
        <div
          className="grid grid-cols-3 rounded-2xl bg-[#1a1540] p-3"
          style={{
            width: containerW,
            height: containerH,
            gap: GRID_GAP,
          }}
        >
          {Array.from({ length: 9 }).map((_, i) => (
            <ScratchSurface
              key={i}
              style={{ width: cellW, height: cellH }}
            >
              {(i === 0 || i === 4 || i === 8) && icon}
            </ScratchSurface>
          ))}
        </div>
      )}

      {mecanica === "elije" && (
        <div className="flex items-center justify-center" style={{ gap: 24 }}>
          {[0, 1, 2].map((i) => (
            <ScratchSurface
              key={i}
              className={i === 1 ? "ring-2 ring-[#a78bfa]" : "opacity-70"}
              style={
                i === 1
                  ? {
                      width: panelW * 0.28,
                      height: panelH * 0.92,
                    }
                  : {
                      width: panelW * 0.22,
                      height: panelH * 0.72,
                    }
              }
            >
              {icon}
            </ScratchSurface>
          ))}
        </div>
      )}
    </div>
  );
}
